import React, { useMemo } from 'react';
import classNames from 'classnames';
import css from './MvpListingCard.module.css';
import { useAuth } from '../../context/AuthContext';
import { toggleLike } from '../../api/auth';

const formatPrice = (price, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  } catch (e) {
    return `${currency} ${price}`;
  }
};

const ROAST_BADGE_LINKS = {
  light: '/coffee/collections/best-light-roast-coffee',
  medium: '/coffee/collections/best-medium-roast-coffee',
  decaf: '/coffee/collections/decaf-coffee-beans',
  dark: '/coffee/collections/best-dark-roast-coffee',
  espresso: '/coffee/collections/best-espresso-beans',
};

const normalizeBadgeKey = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const safeString = v => (typeof v === 'string' ? v.trim() : '');
const isNonEmpty = s => typeof s === 'string' && s.trim().length > 0;

const roastVariantFor = value => {
  const v = safeString(value).toLowerCase();

  if (v === 'light' || v === 'omni') return 'roastLight';
  if (v === 'medium') return 'roastMedium';
  if (v === 'dark' || v === 'espresso') return 'roastDark';

  return 'default';
};

const Badge = ({ children, variant = 'default', href = null }) => {
  const computedVariant = variant === 'roast' ? roastVariantFor(children) : variant;

  const variantClass =
    computedVariant === 'decaf'
      ? css.badgeDecaf
      : computedVariant === 'stock'
      ? css.badgeStock
      : computedVariant === 'offer'
      ? css.badgeOffer
      : computedVariant === 'roastLight'
      ? css.badgeRoastLight
      : computedVariant === 'roastMedium'
      ? css.badgeRoastMedium
      : computedVariant === 'roastDark'
      ? css.badgeRoastDark
      : css.badgeDefault;

  if (href) {
    return (
      <a href={href} className={classNames(css.badge, css.badgeLink, variantClass)}>
        {children}
      </a>
    );
  }

  return <span className={classNames(css.badge, variantClass)}>{children}</span>;
};

const titleCase = s => {
  const t = safeString(s).toLowerCase();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const junkNotePatterns = [
  /^blend color/i,
  /^blend$/i,
  /^blends$/i,
  /^classic$/i,
  /^standalone$/i,
  /^coffee$/i,
  /^filter:/i,
  /^coffee type:/i,
  /^house blends$/i,
  /#/,
  /\d/,
  /_/,
  /\bbadge\b/i,
  /\bbestseller\b/i,
  /\bstaff\b/i,
  /\bgrind\b/i,
  /\bsize\b/i,
  /\boz\b/i,
  /\blbs?\b/i,
  /\bretail\b/i,
  /\bbackorder\b/i,
  /\bautomatic drip\b/i,
  /\bmultigift\b/i,
  /\bspo-default\b/i,
  /\bsubscription\b/i,
  /\bcollection\b/i,
  /\ball products\b/i,
  /\bactive\b/i,
  /\baccessories\b/i,
  /\btab\d*:/i,
  /^_tab/i,
  /^__/,
];

const cleanFlavorNote = note => {
  const s = safeString(note);
  if (!s) return null;
  if (s.length > 36) return null;
  if (junkNotePatterns.some(re => re.test(s))) return null;
  return s;
};

const normalizeOfferLabel = listing => {
  if (!listing?.has_offer || !listing?.is_discounted) return null;
  return '★ Sale';
};

const normalizePopularLabel = listing => {
  if (!listing?.popular_local) return null;
  return '🔥 Popular';
};

const normalizeFreshLabel = listing => {
  if ((!listing?.is_new && !listing?.is_fresh) || !listing?.first_seen_at) return null;
  return '✨ New';
};

const handleOutboundClick = href => {
  try {
    const seen = window.localStorage.getItem('bp_outbound_notice_seen');

    if (!seen) {
      window.localStorage.setItem('bp_outbound_notice_seen', '1');

      const confirmed = window.confirm(
        'You are about to visit the roaster site to view or purchase this coffee.'
      );

      if (!confirmed) return;
    }

    window.open(href, '_blank', 'noopener,noreferrer');
  } catch (e) {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
};

const MvpListingCard = props => {
  const { listing, className } = props;
  const { likes, setLikes, me } = useAuth();

  const {
    product_id,
    roast_name,
    roaster_name,
    canonical_url,
    image_url,
    roast_level,
    decaf,
    in_stock,
    flavor_notes = [],
    flavor_families = [],
    min_price,
    display_price,
    display_price_is_from,
    bag_size_label,
    price_per_oz,
    currency,
    pull_count,
    pigeon_pick,
  } = listing || {};

  const titleText = isNonEmpty(roast_name) ? roast_name : 'Untitled roast';
  const roasterText = isNonEmpty(roaster_name) ? roaster_name : 'Roaster';

  const trackedHref = product_id ? `/api/out/${encodeURIComponent(product_id)}` : null;
  const fallbackHref = safeString(canonical_url) || '#';
  const href = trackedHref || fallbackHref;
  const isRealLink = href !== '#';

  const likeId = useMemo(() => {
    return product_id || safeString(canonical_url) || `${roasterText}-${titleText}`;
  }, [product_id, canonical_url, roasterText, titleText]);

  const liked = likes.includes(likeId);

  const handleLike = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (!me) {
      window.location.href = '/login';
      return;
    }

    try {
      await toggleLike(likeId);
      setLikes(prev =>
        prev.includes(likeId) ? prev.filter(id => id !== likeId) : [...prev, likeId]
      );
    } catch (err) {
      console.error('toggleLike failed', err);
    }
  };

  const freshPill = normalizeFreshLabel(listing);
  const popularPill = normalizePopularLabel(listing);
  const offerPill = normalizeOfferLabel(listing);

  const familyPills = Array.isArray(flavor_families)
    ? flavor_families
        .filter(family => family?.label && family?.collection_url)
        .slice(0, 2)
    : [];

  const legacyNotePills = Array.isArray(flavor_notes)
    ? flavor_notes
        .map(cleanFlavorNote)
        .filter(Boolean)
        .slice(0, 2)
        .map(label => ({ label, kind: 'flavor' }))
    : [];

  // New API responses use normalized families. The legacy fallback prevents
  // empty cards while an older cached response is still in circulation.
  const overlayPills = [
    ...(freshPill ? [{ label: freshPill, kind: 'fresh' }] : []),
    ...(popularPill ? [{ label: popularPill, kind: 'popular' }] : []),
    ...(offerPill ? [{ label: offerPill, kind: 'offer' }] : []),
    ...(familyPills.length
      ? familyPills.map(family => ({
          label: family.label,
          href: family.collection_url,
          kind: 'flavor',
        }))
      : legacyNotePills),
  ].slice(0, 3);

  const effectivePrice = Number.isFinite(display_price)
    ? display_price
    : Number.isFinite(min_price)
    ? min_price
    : null;

  const badgeRoast =
    roast_level && safeString(roast_level).toLowerCase() !== 'unknown'
      ? titleCase(roast_level)
      : null;

  const badgeDecaf = decaf ? 'Decaf' : null;
  const badgeStock = in_stock ? 'In stock' : null;

  const roastBadgeHref = badgeRoast
    ? ROAST_BADGE_LINKS[normalizeBadgeKey(roast_level)]
    : null;

  return (
    <div className={classNames(css.card, className)} aria-label={`${titleText} by ${roasterText}`}>
      <div className={css.imageWrap}>
        {image_url ? <img className={css.image} src={image_url} alt="" loading="lazy" /> : null}

        {pigeon_pick ? (
          <a
            href="/pigeon-picks"
            className={css.pigeonPickBadge}
            onClick={e => e.stopPropagation()}
            aria-label="View Bean Pigeon Picks"
          >
            <img
              src="/picks/pigeon-pick-badge.png"
              alt=""
              className={css.pigeonPickBadgeImage}
              aria-hidden="true"
            />
          </a>
        ) : null}

        <div className={css.overlayTopLeft}>
          {overlayPills.map((pill, i) => {
            const pillClassName = classNames(
              css.overlayPill,
              pill.kind === 'offer' ? css.overlayOfferPill : null,
              pill.kind === 'popular' ? css.overlayPopularPill : null,
              pill.kind === 'fresh' ? css.overlayNewPill : null
            );

            return pill.href ? (
              <a
                key={`${pill.label}-${i}`}
                href={pill.href}
                className={pillClassName}
                onClick={e => e.stopPropagation()}
              >
                {pill.label}
              </a>
            ) : (
              <span key={`${pill.label}-${i}`} className={pillClassName}>
                {pill.label}
              </span>
            );
          })}
        </div>

        <div className={css.overlayTopRight}>
          <button
            type="button"
            className={classNames(css.heartButton, liked ? css.heartButtonActive : null)}
            onClick={handleLike}
            aria-label={liked ? 'Saved' : 'Save'}
            aria-pressed={liked}
          >
            <span className={classNames(css.heartIcon, liked ? css.heartIconActive : null)}>
              {liked ? '♥' : '♡'}
            </span>
          </button>
        </div>
      </div>

      <div className={css.body}>
        <div className={css.titleRow}>
          <div className={css.title} title={titleText}>
            {isRealLink ? (
              <a
                href={href}
                rel="nofollow external"
                className={css.titleLink}
                onClick={e => {
                  e.preventDefault();
                  handleOutboundClick(href);
                }}
              >
                {titleText}
              </a>
            ) : (
              titleText
            )}
          </div>

          {Number.isFinite(effectivePrice) ? (
            <div className={css.priceBlock}>
              <div className={css.price}>
                {display_price_is_from ? 'From ' : ''}
                {formatPrice(effectivePrice, currency || 'USD')}
                {bag_size_label ? ` · ${bag_size_label}` : ''}
              </div>
              {Number.isFinite(price_per_oz) ? (
                <div className={css.pricePerOz}>
                  {formatPrice(price_per_oz, currency || 'USD')}/oz
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={css.subtitle} title={roasterText}>
          {roasterText}
        </div>
        {Number(pull_count) > 0 ? <div className={css.pullCount}>☕ {Number(pull_count)} {Number(pull_count) === 1 ? 'pull' : 'pulls'}</div> : null}

        <div className={css.badgesRow}>
          {badgeRoast ? (
            <Badge variant="roast" href={roastBadgeHref}>
              {badgeRoast}
            </Badge>
          ) : null}

          {badgeDecaf ? (
            <button
              type="button"
              className={css.badgeLinkButton}
              aria-label="View decaf coffee beans"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();

                if (typeof window !== 'undefined') {
                  window.location.assign('/coffee/collections/decaf-coffee-beans');
                }
              }}
            >
              <Badge variant="decaf">{badgeDecaf}</Badge>
            </button>
          ) : null}
          {badgeStock ? <Badge variant="stock">{badgeStock}</Badge> : null}
        </div>
      </div>
    </div>
  );
};

export default MvpListingCard;